import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  getDaysSince,
  getDownloadOffer,
  getUsageOffer,
  getWelcomeOffer,
  UPSELL_WELCOME_WINDOW_DAYS,
} from '@/entities/pricing';
import { useCurrentSpaceId } from '@/entities/teamspace';

import { PATHS } from '@/shared/constants/routes/paths';
import { isAnyModalOpen, subscribeToModalClose } from '@/shared/lib/modal';
import { getPathSpaceId, ResultDownload, useResultDownloadStore } from '@/shared/lib/resultDownload';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PlanCategory } from '@/shared/types/Billing';

import { getUpsellAnalytics } from './analytics';
import { UpsellHighlight, useUpsellModalStore, useUpsellTriggerStore } from './useUpsellModalStore';
import { UpsellSnapshot, useUpsellSnapshot } from './useUpsellSnapshot';

const AFTER_DOWNLOAD_DELAY_MS = 800;
const MODAL_RETRY_DELAY_MS = 600;
const SILENT_PATHS = [PATHS.pricing, PATHS.checkout, PATHS.account].map((path) => `/${path}`);
const SPACE_INDEX_PATH = new RegExp(`^/${PATHS.spaces}/[^/]+/?$`);
const MAX_DOWNLOAD_AGE_MS = 30_000;

type Trigger = { download?: ResultDownload; type: 'download' | 'home' | 'navigation' };

const isPaidProject = (download: ResultDownload, snapshot: UpsellSnapshot) => {
  const { project } = download;

  return (
    !project.is_demo &&
    !project.free_dubbing &&
    Date.parse(project.status_date) >= Date.parse(snapshot.subscription.created)
  );
};

const getHighlight = (download: ResultDownload, snapshot: UpsellSnapshot): undefined | UpsellHighlight => {
  if (snapshot.context.category !== PlanCategory.Creator) return undefined;

  const { kind, project } = download;

  if (kind === 'lipsync' || project.lipsync_info?.paid_lipsync_done || project.lipsync_info?.lipsync_result_path) {
    return 'lipsync';
  }
  if (kind === 'subtitles' || project.subtitles_info?.subtitles_burn_result_path) return 'subtitles';

  return undefined;
};

type Gate = 'blocked' | 'open' | 'wait';

const getSpaceOwnership = (snapshot: UpsellSnapshot, spaceId?: string) => {
  if (!spaceId) return 'own';
  if (!snapshot.ownSpaceIds.length) return 'unknown';

  return snapshot.ownSpaceIds.includes(spaceId) ? 'own' : 'other';
};

export const getGate = (snapshot: UpsellSnapshot, pathname: string, sourceSpaceId?: string): Gate => {
  const ownership = [getPathSpaceId(pathname), sourceSpaceId].map((spaceId) => getSpaceOwnership(snapshot, spaceId));

  if (ownership.includes('other') || SILENT_PATHS.some((path) => pathname.startsWith(path)) || snapshot.isWriteError) {
    return 'blocked';
  }

  return ownership.includes('unknown') || isAnyModalOpen() || !snapshot.isUsageReady ? 'wait' : 'open';
};

const isForeign = (snapshot: UpsellSnapshot, trigger: null | Trigger) =>
  getSpaceOwnership(snapshot, trigger?.download?.spaceId) === 'other';

const TRIGGER_RANK: Record<Trigger['type'], number> = { download: 2, home: 1, navigation: 0 };

const pickTrigger = (pending: null | Trigger, next: Trigger) =>
  pending && TRIGGER_RANK[pending.type] > TRIGGER_RANK[next.type] ? pending : next;

export const openModal = (snapshot: UpsellSnapshot, trigger: Trigger) => {
  const { context, isFirstMonth, storage, subscription, usage, update } = snapshot;
  const { open } = useUpsellModalStore.getState();

  if (trigger.type !== 'navigation' && usage?.isCrossed && storage.usagePeriod !== usage.periodKey) {
    const selection = getUsageOffer(context, isFirstMonth);

    if (selection) {
      update({ usagePeriod: usage.periodKey });
      open({ context, flow: 'usage', primary: selection.primary, secondary: selection.secondary ?? undefined, usage });
      return true;
    }
  }

  if (trigger.download && !storage.downloadShown && isPaidProject(trigger.download, snapshot)) {
    const offer = getDownloadOffer(context);

    if (offer) {
      update({ downloadShown: true });
      open({
        context,
        downloadKind: trigger.download.kind,
        flow: 'download',
        highlight: getHighlight(trigger.download, snapshot),
        primary: offer,
      });
      return true;
    }
  }

  if (
    trigger.type !== 'download' &&
    !storage.welcomeShown &&
    getDaysSince(subscription.created, Date.now()) < UPSELL_WELCOME_WINDOW_DAYS
  ) {
    const offer = getWelcomeOffer(context);

    if (offer !== undefined) {
      update({ welcomeShown: true });
      open({ context, flow: 'welcome', primary: offer ?? undefined });
      return true;
    }
  }

  return false;
};

export const useUpsellModalTriggers = () => {
  const snapshot = useUpsellSnapshot({ writesPurchase: true });
  const { pathname } = useLocation();
  const homeVisits = useUpsellTriggerStore((state) => state.homeVisits);
  const lastDownload = useResultDownloadStore((state) => state.last);
  const [modalClosedAt, setModalClosedAt] = useState(0);
  const handledHomeVisits = useRef(0);
  const handledDownloadAt = useRef(lastDownload?.at ?? 0);
  const pendingTrigger = useRef<null | Trigger>(null);
  const tryOpenRef = useRef<(trigger: Trigger) => void>(() => undefined);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => subscribeToModalClose(() => setModalClosedAt(Date.now())), []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const tryOpen = useCallback(
    (next: Trigger) => {
      if (!snapshot) return;
      if (isForeign(snapshot, pendingTrigger.current)) pendingTrigger.current = null;
      if (isForeign(snapshot, next)) return;

      const trigger = pickTrigger(pendingTrigger.current, next);
      const gate = getGate(snapshot, pathname, trigger.download?.spaceId);

      pendingTrigger.current = gate === 'wait' ? trigger : null;

      if (gate !== 'open' || !openModal(snapshot, trigger)) return;

      const { flow, primary } = useUpsellModalStore.getState();
      if (flow) MixpanelEvents.upsellModalShown(getUpsellAnalytics(snapshot.context, flow, primary));
    },
    [pathname, snapshot],
  );

  useEffect(() => {
    tryOpenRef.current = tryOpen;
  }, [tryOpen]);

  const schedule = (callback: () => void, delay: number) => {
    timers.current.push(setTimeout(callback, delay));
  };

  useEffect(() => {
    if (pendingTrigger.current?.type === 'home') pendingTrigger.current = null;
  }, [pathname]);

  useEffect(() => {
    const visits = useUpsellTriggerStore.getState().homeVisits;

    if (!snapshot) {
      if (visits !== handledHomeVisits.current) {
        handledHomeVisits.current = visits;
        pendingTrigger.current = pickTrigger(pendingTrigger.current, { type: 'home' });
      }
      return;
    }

    const isHomeVisit = visits !== handledHomeVisits.current;
    const isTriggerWaiting = (pendingTrigger.current?.type ?? 'navigation') !== 'navigation';

    if (!isHomeVisit && !isTriggerWaiting && SPACE_INDEX_PATH.test(pathname)) return;

    handledHomeVisits.current = visits;
    tryOpen({ type: isHomeVisit ? 'home' : 'navigation' });
  }, [homeVisits, pathname, snapshot, tryOpen]);

  useEffect(() => {
    if (!modalClosedAt || !pendingTrigger.current) return;

    schedule(() => {
      if (pendingTrigger.current) tryOpenRef.current(pendingTrigger.current);
    }, MODAL_RETRY_DELAY_MS);
  }, [modalClosedAt]);

  useEffect(() => {
    if (snapshot === undefined || !lastDownload || lastDownload.at === handledDownloadAt.current) return;

    handledDownloadAt.current = lastDownload.at;
    if (!snapshot || Date.now() - lastDownload.at > MAX_DOWNLOAD_AGE_MS) return;

    schedule(() => tryOpenRef.current({ download: lastDownload, type: 'download' }), AFTER_DOWNLOAD_DELAY_MS);
  }, [lastDownload, snapshot]);
};

export const useUpsellHomeVisit = () => {
  const spaceId = useCurrentSpaceId();

  useEffect(() => {
    useUpsellTriggerStore.setState((state) => ({ homeVisits: state.homeVisits + 1 }));
  }, [spaceId]);
};
